import React, { useCallback, useMemo, useState } from 'react';
import { GridIcon } from '@tanium/react-data-grid';
import { useTranslation } from 'react-i18next';
import { RibbonColumn, SortChangeEvent, SortItem, StackedRibbonListStatic } from '@tanium/react-stacked-ribbon-list';
import { PageSection } from '@tanium/react-page-section';
import { Stack } from '@tanium/react-stack';
import { Box } from '@tanium/react-box';
import { Shimmer } from '@tanium/react-shimmer';
import { TextWithIcon } from '@tanium/react-text-with-icon';
import { TooltippedButton } from '@tanium/react-button';
import { PersonIcon, EditIcon, TrashIcon } from '@tanium/react-graphics';
import { useIdentityProviderConfigs } from '../../hooks/useIdentityProviderConfigs';
import { AsyncStatus } from '../../hooks/useAsync';
import { IdpSetupModal } from '../../components/IdpModals/IdpSetupModal';
import { DeleteIdpModal } from '../../components/IdpModals/IdpDeleteModal';
import { useModalState } from '../../hooks/useModalState';
import { AsyncSuspense } from '../../components/AsyncSuspense';
import { sortData } from '../../utilities/commonFuncs';
import { ConfigureIdpRequest, IdpResponse } from '../../models/IdpModels';
import { useHasPermissions } from '../../hooks/useUserPermissions';
import { useTenantDomains } from '../../hooks/useTenantDomains';
import { Domain, DomainStatus } from '../../types';
import { Permission } from '../../models/Permissions';

export const IdentityProvidersSection: React.VFC = () => {
  const { t } = useTranslation();
  const [dataSort, setDataSort] = useState<SortItem[]>([]);
  const { loadIdps, createIdp, updateIdp } = useIdentityProviderConfigs();
  const isLoading = loadIdps.status === AsyncStatus.Pending;
  const idpConfigurations = useMemo(() => sortData(dataSort, loadIdps.value ?? []), [loadIdps.value, dataSort]);
  const dataGetter = (i: number): IdpResponse => idpConfigurations[i];
  const { tenantDomains } = useTenantDomains();

  const addOrUpdateModal = useModalState<ConfigureIdpRequest>();
  const deleteModal = useModalState<IdpResponse>();

  const canCreateOrUpdateIdp = useHasPermissions([Permission.CreateIdp, Permission.UpdateIdp]);
  const canDeleteIdp = useHasPermissions([Permission.DeleteIdp]);

  const hasVerifiedDomains = useCallback((domains: Domain[]): boolean => {
    for (const domain of domains) {
      if (domain.verificationStatus === DomainStatus.SUCCESS) {
        return true;
      }
    }
    return false;
  }, []);

  const tooltipAddIDP = useMemo((): string => {
    if (canCreateOrUpdateIdp) {
      if (hasVerifiedDomains(tenantDomains)) {
        return '';
      }
      return t('You must have a verified domain to add an IDP.');
    }
    return t('You have insufficient permissions to add an IDP.');
  }, [tenantDomains, canCreateOrUpdateIdp, hasVerifiedDomains, t]);

  const onSortChange = useCallback(({ sort }: SortChangeEvent) => {
    setDataSort(sort);
  }, []);

  const columns: RibbonColumn<IdpResponse>[] = [
    {
      label: t('Name'),
      dataKey: 'displayName',
    },
    {
      label: t('Sign In Email Domains'),
      dataKey: 'domainNames',
      renderer: ({ itemData }) => itemData.domainNames?.join(', '),
    },
    {
      label: t('Actions'),
      dataKey: '',
      alignment: 'center',
      width: 80,
      renderer: (field) => (
        <span>
          <Stack itemSpacing={2}>
            {canCreateOrUpdateIdp && (
              <GridIcon
                icon={EditIcon}
                label={t('Edit')}
                onClick={() => {
                  addOrUpdateModal.openModal(field.itemData);
                }}
              />
            )}
            {canDeleteIdp && (
              <GridIcon
                icon={TrashIcon}
                label={t('Delete')}
                onClick={() => {
                  deleteModal.openModal(field.itemData);
                }}
              />
            )}
          </Stack>
        </span>
      ),
      isSortDisabled: true,
    },
  ];

  return (
    <PageSection
      title={<TextWithIcon leadingIcon={PersonIcon}>{t('Identity Provider Settings')}</TextWithIcon>}
      additionalTitleContent={
        <Stack width="100%" direction="row" justifyContent="right" alignItems="center">
          <TooltippedButton
            size="sm"
            disabled={!canCreateOrUpdateIdp || !hasVerifiedDomains(tenantDomains)}
            tooltipContent={tooltipAddIDP}
            onClick={() => {
              addOrUpdateModal.openModal();
            }}
          >
            {t('Add IDP')}
          </TooltippedButton>
        </Stack>
      }
    >
      <AsyncSuspense
        state={loadIdps}
        loading={
          <Box mt={2} height={1} width="100%">
            <Shimmer />
          </Box>
        }
        failure={t('Failed to load Identity Provider Settings')}
      >
        <StackedRibbonListStatic<string, IdpResponse>
          columns={columns}
          dataGetter={dataGetter}
          isItemLoaded={() => !isLoading}
          itemCount={idpConfigurations.length}
          totalItemCount={idpConfigurations.length}
          itemIdGetter={(i) => dataGetter(i)?.displayName}
          isLoading={isLoading}
          onSortChange={onSortChange}
          sort={dataSort}
        ></StackedRibbonListStatic>
      </AsyncSuspense>
      <IdpSetupModal
        initialConfig={addOrUpdateModal.data}
        isOpen={addOrUpdateModal.isModalOpen}
        onRequestClose={() => {
          createIdp.reset();
          updateIdp.reset();
          addOrUpdateModal.closeModal();
        }}
      />
      <DeleteIdpModal idp={deleteModal.data} isOpen={deleteModal.isModalOpen} onRequestClose={deleteModal.closeModal} />
    </PageSection>
  );
};
